import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { LogOut, PawPrint } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Step, Progress } from "./ui";
import { Welcome, Mobile, Confirm, NotMe, Details } from "./StepsIdentity";
import { Pets, PetForm, PetDetails, Notes } from "./StepsPets";
import { Services } from "./StepsServices";
import { When } from "./StepsWhen";
import { Review, Done } from "./StepsReview";
import { ReceptionistPanel } from "./ReceptionistPanel";
import "./selfbooking.css";

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const blank = () => ({
  mobile: "", matches: [], customer: null, isNew: false,
  cust: { name: "", pin: "", state: "", city: "", area: "", location_id: "" },
  selected: [], newPets: [], petMissing: {}, petFix: {}, notes: "", tags: [], items: [],
  date: "", time: "", check_in: "", check_out: "", reasons: [],
  created: { customer_id: "", petMap: {}, completed: false }, reqId: uid(),
});
const ORDER = ["welcome", "mobile", "confirm", "details", "pets", "petdetails", "notes", "services", "when", "review", "done"];

function ExitButton({ onExit }) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = async () => { setBusy(true); try { await onExit(); } finally { setBusy(false); } };
  return (
    <>
      <button type="button" onClick={() => setAsk(true)} aria-label="Exit Self Booking and log out" className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-sm font-semibold text-[var(--sb-muted)] hover:bg-white" data-testid="sb-exit"><LogOut className="h-4 w-4" /> Exit</button>
      {ask && createPortal(
        <div style={{ background: "rgba(0,0,0,0.4)", minHeight: 0 }} className="sb-root fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="sb-exit-title" data-testid="sb-exit-dialog">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-xl">
            <h2 id="sb-exit-title" className="text-lg font-semibold text-[var(--sb-plum)]">Exit Self Booking and log out?</h2>
            <p className="mt-2 text-sm text-[var(--sb-muted)]">You'll be signed out of Pawkt and need to log in again. Bookings and customer records are not affected.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" autoFocus disabled={busy} onClick={() => setAsk(false)} className="min-h-[44px] rounded-full px-5 font-semibold text-[var(--sb-plum)] hover:bg-slate-50" data-testid="sb-exit-cancel">Cancel</button>
              <button type="button" disabled={busy} onClick={confirm} className="min-h-[44px] rounded-full bg-[var(--sb-plum)] px-5 font-semibold text-white disabled:opacity-50" data-testid="sb-exit-confirm">{busy ? "Logging out…" : "Log Out"}</button>
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
}

export default function SelfBooking() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(blank);
  const [hist, setHist] = useState(["welcome"]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const top = useRef(null);
  const step = hist[hist.length - 1];
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const go = (s) => { setErr(""); setHist((h) => [...h, s]); };
  const back = () => { setErr(""); setHist((h) => (h.length > 1 ? h.slice(0, -1) : h)); };
  const restart = () => { setF(blank()); setHist(["welcome"]); setErr(""); };
  const cat = useQuery({ queryKey: ["sb-catalog"], queryFn: async () => (await api.get("/self-booking/catalog")).data, staleTime: 300e3 });

  useEffect(() => { top.current?.scrollIntoView({ block: "start" }); }, [step]);
  useEffect(() => {
    // keep the focused field visible above the on-screen keyboard (visualViewport shrinks when it opens)
    const reveal = () => {
      const el = document.activeElement;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 250);
    };
    const vv = window.visualViewport;
    document.addEventListener("focusin", reveal);
    vv?.addEventListener("resize", reveal);
    return () => { document.removeEventListener("focusin", reveal); vv?.removeEventListener("resize", reveal); };
  }, []);
  useEffect(() => {
    if (step !== "done") return undefined;
    const t = setTimeout(restart, 90000);
    return () => clearTimeout(t);
  }, [step]);

  const custPets = f.customer?.pets || [];
  const allPets = [...custPets, ...f.newPets.map((p) => ({ ...p, id: p.tmp }))];
  const visiting = allPets.filter((p) => f.selected.includes(p.id));
  const firstName = f.isNew ? f.cust.name.trim().split(" ")[0] : f.customer?.first_name || f.cust.name.trim().split(" ")[0];
  const depts = new Set(f.items.map((i) => i.dept));
  const needDay = [...depts].some((d) => d !== "boarding");
  const needStay = depts.has("boarding");
  const needVet = depts.has("vet");
  const reasonsCat = cat.data?.visit_reasons || [];

  const lookup = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/self-booking/lookup", { mobile: f.mobile });
      if (data.customers.length) { set({ matches: data.customers, isNew: false }); go("confirm"); }
      else { set({ matches: [], customer: null, isNew: true, selected: [], newPets: [] }); go("details"); }
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };
  const pick = (c) => {
    set({ customer: c, isNew: false, selected: c.pets.length === 1 ? [c.pets[0].id] : [], newPets: [] });
    go(c.missing.length ? "details" : "pets");
  };
  const afterPets = async () => {
    const existing = f.selected.filter((id) => custPets.some((p) => p.id === id));
    if (!existing.length || !f.customer) return go("notes");
    setBusy(true);
    try {
      const { data } = await api.get("/self-booking/readiness", { params: { customer_id: f.customer.id, pet_ids: existing.join(",") } });
      const pm = {};
      data.missing.filter((m) => m.entity === "pet").forEach((m) => { pm[m.id] = [...(pm[m.id] || []), m.field]; });
      set({ petMissing: pm });
      go(Object.keys(pm).length ? "petdetails" : "notes");
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const created = { ...f.created, petMap: { ...f.created.petMap } };
      const petBody = (p) => ({ name: p.name, species: p.species, breed: p.breed, gender: p.gender, age_years: p.age_years, age_months: p.age_months, neuter_status: p.neuter_status, vaccination: { acknowledgement: "unknown" } });
      const todo = f.newPets.filter((p) => f.selected.includes(p.tmp) && !created.petMap[p.tmp]);
      if (f.isNew && !created.customer_id) {
        const { data } = await api.post("/customers/with-pets", {
          customer: { name: f.cust.name.trim(), mobile: f.mobile, pin: f.cust.pin, state: f.cust.state, city: f.cust.city, area: f.cust.area,
            location_id: f.cust.location_id, country: "India", source: "walk_in" }, pets: todo.map(petBody) },
        { headers: { "Idempotency-Key": `${f.reqId}-c` } });
        created.customer_id = data.id;
        todo.forEach((p, i) => { created.petMap[p.tmp] = data.pets[i].id; });
      } else {
        created.customer_id = f.customer.id;
        if (f.customer.missing.length && !created.completed) {
          await api.post(`/self-booking/customers/${f.customer.id}/complete`, f.cust);
          created.completed = true;
        }
        if (todo.length) {
          const { data } = await api.post(`/customers/${f.customer.id}/pets`, { pets: todo.map(petBody) });
          todo.forEach((p, i) => { created.petMap[p.tmp] = data.pets[i].id; });
        }
        for (const pid of Object.keys(f.petMissing).filter((id) => f.selected.includes(id))) {
          await api.post(`/self-booking/pets/${pid}/complete`, f.petFix[pid] || {});
        }
      }
      set({ created });
      const pid = (id) => created.petMap[id] || id;
      const petIds = f.selected.map(pid);
      const VAX = { yes: "up to date", no: "not up to date", unsure: "not sure" };
      const vaxNotes = f.newPets.filter((p) => f.selected.includes(p.tmp) && p.vax).map((p) => `${p.name} vaccinations (owner says): ${VAX[p.vax]}`);
      const notes = [...f.tags, f.notes.trim(), ...vaxNotes].filter(Boolean).join(" · ");
      await api.post("/self-booking/requests", {
        request_id: f.reqId, customer_id: created.customer_id, pet_ids: petIds,
        items: petIds.flatMap((p) => f.items.map((i) => ({ pet_id: p, service_id: i.service_id }))),
        date: needDay ? f.date : "", time: f.time, check_in: needStay ? f.check_in : "", check_out: needStay ? f.check_out : "",
        visit_reasons: needVet ? f.reasons : [], notes,
      });
      go("done");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const st = e?.response?.status;
      setErr(st === 409 && d?.candidates ? "It looks like you may already be registered with us. Please ask reception to help you finish."
        : st === 422 || !st || st >= 500 ? "Something didn't go through. Please try again, or ask reception for help." : apiErr(e));
    } finally { setBusy(false); }
  };

  const idx = ORDER.indexOf(step);
  const screens = {
    welcome: <Welcome business={user?.tenant?.name} onStart={() => go("mobile")} />,
    mobile: <Mobile value={f.mobile} onChange={(mobile) => set({ mobile })} onNext={lookup} busy={busy} error={err} />,
    confirm: <Confirm matches={f.matches} onPick={pick} onNotMe={() => go("notme")} onBack={back} />,
    notme: <NotMe onBack={back} />,
    details: <Details isNew={f.isNew} need={f.customer?.missing || []} value={f.cust} onChange={(p) => set({ cust: { ...f.cust, ...p } })} onNext={() => go("pets")} onBack={back} />,
    pets: <Pets pets={custPets} newPets={f.newPets} selected={f.selected} busy={busy}
      onToggle={(id) => set({ selected: f.selected.includes(id) ? f.selected.filter((x) => x !== id) : [...f.selected, id] })}
      onAdd={() => go("petform")} onNext={afterPets} onBack={back} />,
    petform: <PetForm onBack={back} onSave={(p) => { set({ newPets: [...f.newPets, p], selected: [...f.selected, p.tmp] }); back(); }} />,
    petdetails: <PetDetails pets={visiting} missing={f.petMissing} fix={f.petFix} onChange={(id, x) => set({ petFix: { ...f.petFix, [id]: { ...(f.petFix[id] || {}), ...x } } })} onNext={() => go("notes")} onBack={back} />,
    notes: <Notes notes={f.notes} tags={f.tags} onChange={set} onNext={() => go("services")} onBack={back} />,
    services: <Services name={firstName} pets={visiting} items={f.items} onChange={(items) => set({ items })} onNext={() => go("when")} onBack={back} />,
    when: <When f={f} set={set} needDay={needDay} needStay={needStay} needVet={needVet} reasons={reasonsCat} onNext={() => go("review")} onBack={back} />,
    review: <Review f={f} name={f.isNew ? f.cust.name.trim() : firstName} pets={visiting} needDay={needDay} needStay={needStay} busy={busy} error={err}
      reasonLabels={f.reasons.map((r) => { const l = reasonsCat.find((x) => x.id === r.id)?.label || r.id; return r.note ? `${l}: ${r.note}` : l; })} onSubmit={submit} onBack={back} />,
    done: <Done name={firstName} onRestart={restart} />,
  };
  return (
    <div className="sb-root" data-testid="self-booking">
      <div ref={top} />
      <header className="sticky top-0 z-20 bg-[var(--sb-ivory)]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <span className="flex min-w-0 items-center gap-2 font-semibold text-[var(--sb-plum)]"><PawPrint className="h-5 w-5 shrink-0" /><span className="sb-serif truncate text-2xl">{user?.tenant?.name || "Welcome"}</span></span>
          <ExitButton onExit={async () => { restart(); await logout(); navigate("/login", { replace: true }); }} />
        </div>
        {step !== "welcome" && step !== "done" && <div className="mx-auto max-w-6xl px-5 sm:px-8"><Progress value={Math.max(idx, 1) / (ORDER.length - 2)} /></div>}
      </header>
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-8 sm:px-8 sm:pt-12">
        <AnimatePresence mode="wait"><Step k={step}>{screens[step]}</Step></AnimatePresence>
      </main>
      <ReceptionistPanel />
    </div>
  );
}
